<template>
  <section ref="grid" class="dynamic-grid" :style="CSS_VARS">
    <slot />
  </section>
</template>

<script>
import Muuri from 'muuri';
import debounce from 'lodash/debounce';

export default {
  name: 'EcsDynamicGrid',

  props: {
    cols: {
      type: Number,
      default: 2,
      validator: propValue => {
        return propValue < 12;
      }
    },
    gap: {
      type: Number,
      default: 16
    }
  },

  data() {
    return {
      colSize: 0,
      resizeObserver: null,
      mutationObserver: null
    };
  },

  computed: {
    CSS_VARS() {
      return {
        '--grid-cols': this.cols,
        '--col-size': `${this.colSize}px`
      };
    }
  },

  mounted() {
    this.setGridSize();
    this.initializeGrid();
    this.mutationObserver = new MutationObserver(this.onGridChange).observe(this.$refs.grid, { childList: true });
    this.resizeObserver = new ResizeObserver(this.onGridResize).observe(this.$refs.grid);
  },

  beforeDestroy() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    delete this.mutationObserver;
    delete this.resizeObserver;
  },

  methods: {
    async initializeGrid() {
      await this.$nextTick();
      if (this.grid) {
        this.grid.destroy();
      }
      this.grid = new Muuri('.dynamic-grid', { itemClass: '.dynamic-grid-item' });
    },

    refreshGrid: debounce(async function() {
      await this.$nextTick();
      if (this.grid) {
        this.grid.refreshItems().layout();
      }
    }, 100),

    setGridSize() {
      if (!this.$refs.grid) {
        return;
      }
      const gridRect = this.$refs.grid.getBoundingClientRect();
      this.colSize = (gridRect.width - this.cols * this.gap) / this.cols;
    },

    onGridResize() {
      this.setGridSize();
    },

    async onGridChange() {
      const itemsToDisplay = [...this.$el.querySelectorAll('.dynamic-grid-item')];
      await this.$nextTick();
      if (itemsToDisplay.length === 0) {
        this.grid.remove(this.grid.getItems());
      } else {
        const currentItems = this.grid.getItems();

        // Remove items that are not showed anymore.
        currentItems.find(currentItem => {
          const currentItemId = currentItem.getElement().getAttribute('id');
          const index = itemsToDisplay.findIndex(item => item.getAttribute('id') === currentItemId);
          if (index === -1) {
            this.grid.remove([currentItem]);
          }
        });

        // Add items that are not showed yet.
        itemsToDisplay.forEach(item => {
          const itemToDisplayId = item.getAttribute('id');
          const index = currentItems.findIndex(item => item.getElement().getAttribute('id') === itemToDisplayId);
          if (index === -1) {
            this.grid.add([item]);
          }
        });
      }
    }
  }
};
</script>

<style src="./scoped.scss" scoped lang="scss" />
